import React, { useState, useEffect } from "react";
import { galleryAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { uploadToFirebase } from "../utils/firebaseUpload";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import ScrollReveal, {
  RevealUp,
  RevealLeft,
  RevealRight,
  RevealScale,
  StaggeredReveal,
} from "../components/ScrollReveal";

const Gallery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("सभी");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchedImages, setFetchedImages] = useState([]);
  
  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    description: "",
    category: "सामान्य",
    file: null,
  });

  // Sanskrit Dohas for Gallery
  const sanskritDohas = [
    {
      sanskrit:
        "चित्रकला मध्ये दृश्यते भक्ति भावना।\nफुलहर धाम की छवियों में दिखता है भक्ति का भावना॥",
      hindi:
        "चित्रकला के माध्यम से दिखती है भक्ति की भावना।\nफुलहर धाम की छवियों में झलकता है दिव्य प्रेम॥",
    },
    {
      sanskrit:
        "दर्शन मात्र से पावन होते हैं मन और आत्मा।\nइन पवित्र छवियों में बसा है राम-सीता का प्रेम॥",
      hindi:
        "दर्शन मात्र से पावन होते हैं मन और आत्मा।\nइन पवित्र छवियों में बसा है राम-सीता का प्रेम॥",
    },
  ];

  const [currentDoha, setCurrentDoha] = useState(0);

  // Real Temple Images with proper categorization and detailed notes
  const templeImages = [
    // गिरिजा स्थान मंदिर (Main Temple)
    {
      _id: "1",
      src: "/images/temple/158934870_106369248200275_1070970614402480772_n.jpg",
      title: "गिरिजा स्थान मंदिर - मुख्य द्वार",
      category: "गिरिजा स्थान",
      description:
        "फुलहर धाम का मुख्य मंदिर जहाँ माता सीता ने गिरिजा (पार्वती) की पूजा की थी",
      notes:
        "यह वही स्थान है जहाँ माता सीता ने विवाह से पूर्व 12 वर्षों तक नियमित पूजा की थी। मंदिर की वास्तुकला मिथिला शैली को दर्शाती है।",
      historicalSignificance: "त्रेतायुग से चली आ रही पूजा परंपरा का केंद्र",
    },
    {
      _id: "2",
      src: "/images/temple/480741525_2188378368226520_2398076682250846950_n.jpg",
      title: "मंदिर परिसर - आंतरिक दृश्य",
      category: "गिरिजा स्थान",
      description: "गिरिजा स्थान मंदिर का आंतरिक भाग और गर्भगृह",
      notes:
        "प्राचीन गर्भगृह में देवी गिरिजा की मूर्ति स्थापित है। यहाँ की दीवारों पर मिथिला चित्रकला के नमूने देखे जा सकते हैं।",
      historicalSignificance: "मूल संरचना सैकड़ों वर्ष पुरानी मानी जाती है",
    },
    {
      _id: "3",
      src: "/images/temple/481108973_2195999984131025_4539895033022794557_n.jpg",
      title: "मंदिर का शिखर और गुंबद",
      category: "गिरिजा स्थान",
      description: "मंदिर की स्थापत्य कला और शिखर का सुंदर दृश्य",
      notes:
        "मंदिर का शिखर मिथिला की पारंपरिक वास्तुकला का उदाहरण है। इसमें हिंदू मंदिर स्थापत्य के सभी तत्व मौजूद हैं।",
      historicalSignificance:
        "राजा जनक के समय की स्थापत्य शैली का प्रतिनिधित्व",
    },

    // पुष्प वाटिका (Flower Garden)
    {
      _id: "4",
      src: "/images/temple/481168107_2196000367464320_3540126543142978603_n.jpg",
      title: "पुष्प वाटिका - प्रथम मिलन स्थल",
      category: "पुष्प वाटिका",
      description: "वह पावन स्थान जहाँ राम और सीता का प्रथम दर्शन हुआ था",
      notes:
        'यह वही वाटिका है जिसका वर्णन तुलसीदास जी ने रामचरितमानस में किया है। "बाग तड़ाग बिलोकि प्रभु, हरषे बंधु समेत।"',
      historicalSignificance: "राम-सीता प्रेम कथा का उद्गम स्थल",
    },
    {
      _id: "5",
      src: "/images/temple/481272698_2196000214131002_7743578382754561207_n.jpg",
      title: "वाटिका में फूलों की क्यारियां",
      category: "पुष्प वाटिका",
      description: "रामायणकालीन फूलों से सजी पुष्प वाटिका",
      notes:
        "वर्तमान में यहाँ गुलाब, चमेली, और अन्य सुगंधित फूल लगाए गए हैं। विकास योजना के तहत रामायणकालीन वनस्पति का रोपण किया जा रहा है।",
      historicalSignificance: "प्राचीन काल से फूलों की खेती का केंद्र",
    },
    {
      _id: "6",
      src: "/images/temple/481469432_2196000274130996_4520467409197620093_n.jpg",
      title: "वाटिका के वृक्ष और हरियाली",
      category: "पुष्प वाटिका",
      description: "पुष्प वाटिका की प्राकृतिक सुंदरता और छायादार वृक्ष",
      notes:
        "यहाँ के पुराने वृक्ष सैकड़ों वर्षों से खड़े हैं। माना जाता है कि इन्हीं वृक्षों की छाया में राम-लक्ष्मण ने विश्राम किया था।",
      historicalSignificance: "प्राचीन वृक्ष जो इतिहास के साक्षी हैं",
    },

    // बाग तराग (Sacred Pond)
    {
      _id: "7",
      src: "/images/temple/158934870_106369248200275_1070970614402480772_n.jpg",
      title: "बाग तराग - पवित्र सरोवर",
      category: "बाग तराग",
      description: "रामायण में वर्णित पवित्र सरोवर जहाँ सीता जी स्नान करती थीं",
      notes:
        "यह वही तड़ाग (सरोवर) है जिसका उल्लेख रामचरितमानस में है। इसका जल आज भी पवित्र माना जाता है और धार्मिक अनुष्ठानों में प्रयोग होता है।",
      historicalSignificance: "त्रेतायुग से अविरल बहता पवित्र जल स्रोत",
    },
    {
      _id: "8",
      src: "/images/temple/481943847_2196608104070213_8151211812422409467_n.jpg",
      title: "सरोवर के किनारे की सुंदरता",
      category: "बाग तराग",
      description: "बाग तराग के चारों ओर की प्राकृतिक सुंदरता",
      notes:
        "सरकारी विकास योजना के तहत इस सरोवर का सौंदर्यीकरण किया जा रहा है। यहाँ कमल के फूल और जलीय पौधे लगाए गए हैं।",
      historicalSignificance: "प्राकृतिक जल संरक्षण का प्राचीन उदाहरण",
    },

    // उत्सव और समारोह (Festivals & Celebrations)
    {
      _id: "9",
      src: "/images/temple/482211008_2196000017464355_3957040181747454731_n.jpg",
      title: "विवाह पंचमी उत्सव",
      category: "उत्सव",
      description:
        "राम-सीता विवाह की स्मृति में मनाया जाने वाला विवाह पंचमी उत्सव",
      notes:
        "अगहन मास की शुक्ल पंचमी को यहाँ भव्य विवाह पंचमी मनाई जाती है। हजारों भक्त इस अवसर पर फुलहर धाम आते हैं।",
      historicalSignificance: "सदियों से चली आ रही परंपरा",
    },
    {
      _id: "10",
      src: "/images/temple/482252646_2202430896821267_199280652644056895_n.jpg",
      title: "मिथिला मध्य परिक्रमा",
      category: "उत्सव",
      description: "मिथिला की सबसे बड़ी धार्मिक यात्रा - मध्य परिक्रमा",
      notes:
        "फाल्गुन मास में 15 दिनों तक चलने वाली यह परिक्रमा भारत-नेपाल के 15 तीर्थ स्थलों को जोड़ती है। फुलहर धाम इसका प्रमुख पड़ाव है।",
      historicalSignificance:
        "मिथिला का महाकुंभ - लाखों श्रद्धालुओं की सहभागिता",
    },
    {
      _id: "11",
      src: "/images/temple/483488386_2202430836821273_3820579133941487252_n.jpg",
      title: "राम नवमी महोत्सव",
      category: "उत्सव",
      description: "भगवान राम के जन्मोत्सव पर आयोजित भव्य समारोह",
      notes:
        "चैत्र मास की शुक्ल नवमी को यहाँ राम नवमी का भव्य आयोजन होता है। भजन-कीर्तन और शोभायात्रा का आयोजन किया जाता है।",
      historicalSignificance: "राम जन्मभूमि से जुड़ाव का प्रतीक",
    },

    // भक्तगण और समुदाय (Devotees & Community)
    {
      _id: "12",
      src: "/images/temple/483525421_2202430830154607_4112882930712722758_n.jpg",
      title: "श्रद्धालुओं का समूह",
      category: "भक्तगण",
      description: "फुलहर धाम आने वाले श्रद्धालुओं का दर्शन",
      notes:
        "यहाँ प्रतिदिन सैकड़ों श्रद्धालु दर्शन के लिए आते हैं। विशेष अवसरों पर यह संख्या हजारों में पहुँच जाती है।",
      historicalSignificance: "निरंतर भक्ति परंपरा का जीवंत उदाहरण",
    },
    {
      _id: "13",
      src: "/images/temple/486349442_2214770365587320_225024915282461657_n.jpg",
      title: "सामुदायिक प्रसाद वितरण",
      category: "भक्तगण",
      description: "मंदिर में आयोजित सामुदायिक भोज और प्रसाद वितरण",
      notes:
        "त्योहारों के अवसर पर यहाँ निःशुल्क भोजन का आयोजन किया जाता है। यह सेवा स्थानीय समुदाय और दानदाताओं के सहयोग से चलती है।",
      historicalSignificance: "अन्नदान की प्राचीन परंपरा",
    },

    // विकास कार्य (Development Work)
    {
      _id: "14",
      src: "/images/temple/486565329_2214770362253987_5895708010638220927_n.jpg",
      title: "मंदिर विकास कार्य",
      category: "विकास",
      description: "₹31.13 करोड़ की विकास योजना के तहत चल रहे निर्माण कार्य",
      notes:
        "बिहार सरकार की स्वीकृत योजना के तहत मंदिर परिसर का आधुनिकीकरण और सुविधाओं का विस्तार किया जा रहा है।",
      historicalSignificance: "2025 में स्वीकृत ऐतिहासिक विकास योजना",
    },
    {
      _id: "15",
      src: "/images/temple/486793037_2214770552253968_1032160347176734457_n.jpg",
      title: "नई सुविधाओं का निर्माण",
      category: "विकास",
      description: "श्रद्धालुओं के लिए नई सुविधाओं का निर्माण कार्य",
      notes:
        "विश्राम गृह, शौचालय, पेयजल व्यवस्था और पार्किंग की सुविधाएं बनाई जा रही हैं। यह कार्य 2026 तक पूरा होने की योजना है।",
      historicalSignificance: "आधुनिक तीर्थ स्थल के रूप में विकास",
    },

    // स्थानीय संस्कृति (Local Culture)
    {
      _id: "16",
      src: "/images/temple/488621803_1108898104614046_400731789014567853_n.jpg",
      title: "मिथिला चित्रकला प्रदर्शन",
      category: "संस्कृति",
      description: "स्थानीय कलाकारों द्वारा मधुबनी पेंटिंग का प्रदर्शन",
      notes:
        "फुलहर धाम मधुबनी चित्रकला की जन्मस्थली मानी जाती है। यहाँ की महिलाएं आज भी इस पारंपरिक कला को जीवित रखे हुए हैं।",
      historicalSignificance: "विश्व प्रसिद्ध मिथिला कला का उद्गम स्थल",
    },
    {
      _id: "17",
      src: "/images/temple/488998154_1109017841268739_6185491843811004585_n.jpg",
      title: "पारंपरिक वेशभूषा में भक्त",
      category: "संस्कृति",
      description: "मिथिला की पारंपरिक वेशभूषा में सजे श्रद्धालु",
      notes:
        "त्योहारों के अवसर पर यहाँ के लोग अपनी पारंपरिक वेशभूषा पहनते हैं। महिलाएं साड़ी और पुरुष धोती-कुर्ता पहनकर आते हैं।",
      historicalSignificance: "मिथिला की सांस्कृतिक पहचान का संरक्षण",
    },

    // प्राकृतिक सौंदर्य (Natural Beauty)
    {
      _id: "18",
      src: "/images/temple/489689121_1109021351268388_5094444913276227651_n.jpg",
      title: "सूर्योदय का दृश्य",
      category: "प्राकृतिक सौंदर्य",
      description: "फुलहर धाम में सूर्योदय का मनोहारी दृश्य",
      notes:
        "प्रातःकाल का समय यहाँ अत्यंत शांत और पवित्र होता है। सूर्योदय के समय मंदिर की घंटियों की आवाज़ वातावरण को दिव्य बना देती है।",
      historicalSignificance: "प्राकृतिक सुंदरता से भरपूर तीर्थ स्थल",
    },
    {
      _id: "19",
      src: "/images/temple/498535700_1142136164623573_2002825225833698687_n.jpg",
      title: "हरियाली से भरा परिसर",
      category: "प्राकृतिक सौंदर्य",
      description: "मंदिर परिसर की हरियाली और प्राकृतिक वातावरण",
      notes:
        "पर्यावरण संरक्षण के लिए यहाँ अनेक वृक्ष लगाए गए हैं। तुलसी, नीम, पीपल और आम के पुराने वृक्ष यहाँ की शोभा बढ़ाते हैं।",
      historicalSignificance: "प्राकृतिक संरक्षण की परंपरा",
    },

    // ऐतिहासिक महत्व (Historical Significance)
    {
      _id: "20",
      src: "/images/temple/514352233_1174036901433499_3284480127729434119_n.jpg",
      title: "प्राचीन शिलालेख",
      category: "ऐतिहासिक",
      description: "मंदिर में स्थित प्राचीन शिलालेख और अभिलेख",
      notes:
        "यहाँ पाए गए शिलालेखों से इस स्थान के प्राचीन इतिहास की जानकारी मिलती है। ये अभिलेख संस्कृत और मैथिली भाषा में हैं।",
      historicalSignificance: "पुरातत्व की दृष्टि से महत्वपूर्ण साक्ष्य",
    },

    // अतिरिक्त मंदिर छवियां (Additional Temple Images)
    {
      _id: "21",
      src: "/images/temple/521445880_1198427715661084_6709078588908529889_n.jpg",
      title: "मंदिर की रात्रि आरती",
      category: "उत्सव",
      description: "संध्या आरती के समय मंदिर का दिव्य दृश्य",
      notes:
        "प्रतिदिन संध्या काल में होने वाली आरती के समय मंदिर में दीपों की रोशनी से अद्भुत वातावरण बनता है। भक्तगण बड़ी संख्या में आरती में सम्मिलित होते हैं।",
      historicalSignificance: "निरंतर चली आ रही आरती परंपरा",
    },
    {
      _id: "22",
      src: "/images/temple/524266980_1198427758994413_5752517791011417799_n.jpg",
      title: "त्योहारी सजावट",
      category: "उत्सव",
      description: "विशेष त्योहारों के अवसर पर मंदिर की सजावट",
      notes:
        "दीपावली, होली और अन्य त्योहारों के समय मंदिर को फूलों और रंगोली से सजाया जाता है। स्थानीय महिलाएं मिलकर पारंपरिक सजावट करती हैं।",
      historicalSignificance: "त्योहारी परंपराओं का संरक्षण",
    },
    {
      _id: "23",
      src: "/images/temple/524392713_2316974265366929_5405429731981149211_n.jpg",
      title: "धार्मिक सभा",
      category: "भक्तगण",
      description: "मंदिर परिसर में आयोजित धार्मिक सभा",
      notes:
        "नियमित रूप से यहाँ धार्मिक प्रवचन और सत्संग का आयोजन होता है। विद्वान संतों द्वारा रामायण और गीता की व्याख्या की जाती है।",
      historicalSignificance: "धार्मिक शिक्षा की अविरल परंपरा",
    },
    {
      _id: "24",
      src: "/images/temple/557613962_1259586969545158_4902008745297679166_n.jpg",
      title: "मंदिर का प्रांगण",
      category: "गिरिजा स्थान",
      description: "विस्तृत मंदिर प्रांगण और आसपास का क्षेत्र",
      notes:
        "मंदिर का विशाल प्रांगण हजारों श्रद्धालुओं को समायोजित कर सकता है। यहाँ बड़े उत्सवों और समारोहों का आयोजन होता है।",
      historicalSignificance: "सामुदायिक गतिविधियों का केंद्र",
    },
    {
      _id: "25",
      src: "/images/temple/583713105_1299011472269374_8699779899208949519_n.jpg",
      title: "पारंपरिक कलाकृति",
      category: "संस्कृति",
      description: "मंदिर में प्रदर्शित पारंपरिक मिथिला कलाकृति",
      notes:
        "मंदिर की दीवारों पर स्थानीय कलाकारों द्वारा बनाई गई मधुबनी पेंटिंग्स देखी जा सकती हैं। ये चित्र राम-सीता की कथा को दर्शाते हैं।",
      historicalSignificance: "मिथिला कला परंपरा का जीवंत उदाहरण",
    },
    {
      _id: "26",
      src: "/images/temple/585341719_2420180751712946_2687495838531834358_n.jpg",
      title: "सामुदायिक कार्यक्रम",
      category: "भक्तगण",
      description: "मंदिर में आयोजित सामुदायिक कार्यक्रम",
      notes:
        "मंदिर नियमित रूप से सामाजिक कल्याण के कार्यक्रम आयोजित करता है। निःशुल्क चिकित्सा शिविर, शिक्षा कार्यक्रम और कौशल विकास के कार्य होते हैं।",
      historicalSignificance: "सामाजिक सेवा की परंपरा",
    },
    {
      _id: "27",
      src: "/images/temple/589605583_1308545874649267_4050356367894589823_n.jpg",
      title: "वृक्षारोपण कार्यक्रम",
      category: "प्राकृतिक सौंदर्य",
      description: "पर्यावरण संरक्षण हेतु वृक्षारोपण",
      notes:
        "मंदिर ट्रस्ट नियमित रूप से वृक्षारोपण कार्यक्रम आयोजित करता है। तुलसी, नीम, पीपल और फलदार वृक्षों का रोपण किया जाता है।",
      historicalSignificance: "पर्यावरण संरक्षण की आधुनिक पहल",
    },
    {
      _id: "28",
      src: "/images/temple/609716271_17946662991066075_120737316173231402_n.jpg",
      title: "ऐतिहासिक दस्तावेज",
      category: "ऐतिहासिक",
      description: "मंदिर के ऐतिहासिक दस्तावेज और अभिलेख",
      notes:
        "मंदिर में संरक्षित प्राचीन दस्तावेज इस स्थान के इतिहास को प्रमाणित करते हैं। ये पांडुलिपियां संस्कृत और मैथिली में लिखी गई हैं।",
      historicalSignificance: "लिखित इतिहास के महत्वपूर्ण साक्ष्य",
    },
    {
      _id: "29",
      src: "/images/temple/610275731_17946663000066075_9164658561053937014_n.jpg",
      title: "पुरातत्व खोज",
      category: "ऐतिहासिक",
      description: "मंदिर परिसर में मिले पुरातत्व अवशेष",
      notes:
        "हाल की खुदाई में यहाँ प्राचीन मूर्तियों और सिक्कों के अवशेष मिले हैं। ये खोजें इस स्थान की प्राचीनता को प्रमाणित करती हैं।",
      historicalSignificance: "पुरातत्व की दृष्टि से अमूल्य खोजें",
    },
    {
      _id: "30",
      src: "/images/temple/610398484_17946662982066075_3020949034458832365_n.jpg",
      title: "भविष्य की योजना",
      category: "विकास",
      description: "मंदिर के भविष्य विकास की योजना",
      notes:
        "2025-2030 की विकास योजना के तहत यहाँ एक संग्रहालय, पुस्तकालय और अनुसंधान केंद्र की स्थापना की जाएगी। यह फुलहर धाम को एक विश्वस्तरीय तीर्थ स्थल बनाने की दिशा में महत्वपूर्ण कदम है।",
      historicalSignificance: "आधुनिक तीर्थ स्थल का भविष्य दृष्टिकोण",
    },
  ];

  const categories = [
    "सभी",
    "गिरिजा स्थान",
    "पुष्प वाटिका",
    "बाग तराग",
    "उत्सव",
    "भक्तगण",
    "विकास",
    "संस्कृति",
    "प्राकृतिक सौंदर्य",
    "ऐतिहासिक",
  ];

  // Fetch images from backend
  useEffect(() => {
    const loadImages = async () => {
      try {
        const response = await galleryAPI.getAll();
        // Assume API returns { images: [...] }
        if (response.data && response.data.images) {
          // Format the fetched images to match our UI structure
          const formatted = response.data.images.map(img => ({
            _id: img._id,
            src: img.url,
            title: img.title,
            category: img.category,
            description: img.description,
            uploadedBy: img.uploadedBy?.name,
            notes: "भक्त द्वारा साझा की गई छवि",
            isUserUpload: true
          }));
          setFetchedImages(formatted);
        }
      } catch (error) {
        console.error("Error fetching gallery images:", error);
      }
    };
    loadImages();
  }, []);

  // Combine hardcoded and fetched images
  const allImages = [...templeImages, ...fetchedImages];

  const filteredImages =
    selectedCategory === "सभी"
      ? allImages
      : allImages.filter((img) => img.category === selectedCategory);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDoha((prev) => (prev + 1) % sanskritDohas.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex =
      direction === "next"
        ? (currentIndex + 1) % filteredImages.length
        : (currentIndex - 1 + filteredImages.length) % filteredImages.length;

    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowRight") navigateImage("next");
    if (e.key === "ArrowLeft") navigateImage("prev");
  };

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    }
  }, [selectedImage, currentIndex]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-temple-bg via-temple-bg-soft to-temple-bg-warm">
      {/* Enhanced Sanskrit Doha Section with Mithila Art Background */}
      <section className="mithila-art-bg text-temple-bg py-8 sm:py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <RevealScale className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-temple-bg/10 rounded-3xl p-6 sm:p-8 backdrop-blur-sm border border-temple-bg/20">
            <h2 className="text-xl sm:text-2xl font-serif font-bold mb-4 sm:mb-6 text-temple-secondary">
              श्लोक
            </h2>
            <div className="space-y-4">
              <p className="text-base sm:text-lg font-serif leading-relaxed">
                {sanskritDohas[currentDoha].sanskrit}
              </p>
              <div className="w-16 sm:w-20 h-1 bg-temple-secondary mx-auto"></div>
              <p className="text-sm sm:text-base opacity-90 leading-relaxed">
                {sanskritDohas[currentDoha].hindi}
              </p>
            </div>
            <div className="flex justify-center space-x-2 mt-4 sm:mt-6">
              {sanskritDohas.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentDoha(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentDoha
                      ? "bg-temple-secondary"
                      : "bg-temple-bg/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </RevealScale>
      </section>

      {/* Enhanced Gallery Header */}
      <section className="py-8 sm:py-12 bg-temple-bg-soft">
        <div className="max-w-5xl mx-auto px-4 sm:px-5 text-center">
          <RevealUp>
            <div className="text-4xl sm:text-5xl md:text-6xl mb-6 text-temple-primary">
              <i className="fas fa-camera"></i>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-temple-text mb-6 leading-tight">
              फुलहर धाम फोटो गैलरी
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-temple-text-muted max-w-4xl mx-auto leading-relaxed">
              सीता राम प्रथम मिलन की पावन भूमि की दुर्लभ छवियां, उत्सवों की
              झलकियां और भक्तों के दर्शन
            </p>
            <div className="w-16 sm:w-20 h-1 bg-temple-primary mx-auto mt-6"></div>
          </RevealUp>

          {/* Gallery Statistics */}
          <ScrollReveal animation="up" delay={300}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-2xl mx-auto">
              <StaggeredReveal>
                <div className="bg-temple-bg-warm rounded-2xl p-4 shadow-temple-sm">
                  <div className="text-2xl font-bold text-temple-primary">
                    {templeImages.length}
                  </div>
                  <div className="text-sm text-temple-text-muted">
                    कुल छवियां
                  </div>
                </div>
                <div className="bg-temple-bg-warm rounded-2xl p-4 shadow-temple-sm">
                  <div className="text-2xl font-bold text-temple-secondary">
                    {categories.length - 1}
                  </div>
                  <div className="text-sm text-temple-text-muted">
                    श्रेणियां
                  </div>
                </div>
                <div className="bg-temple-bg-warm rounded-2xl p-4 shadow-temple-sm">
                  <div className="text-2xl font-bold text-temple-accent">
                    2025
                  </div>
                  <div className="text-sm text-temple-text-muted">नवीनतम</div>
                </div>
                <div className="bg-temple-bg-warm rounded-2xl p-4 shadow-temple-sm">
                  <div className="text-2xl font-bold text-temple-sacred">
                    HD
                  </div>
                  <div className="text-sm text-temple-text-muted">गुणवत्ता</div>
                </div>
              </StaggeredReveal>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Enhanced Category Filter */}
      <section className="py-5 bg-temple-bg sticky top-16 z-40 border-b border-temple-primary/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="flex overflow-x-auto scrollbar-hide gap-3 pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex items-center px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all duration-200 ${
                  selectedCategory === category
                    ? "btn-temple-primary shadow-temple"
                    : "bg-temple-bg-warm text-temple-text hover:bg-temple-bg-soft border border-temple-primary/20"
                }`}
              >
                <span className="mr-2">
                  {category === "सभी" ? (
                    <i className="fas fa-th-large"></i>
                  ) : category === "गिरिजा स्थान" ? (
                    <i className="fas fa-om"></i>
                  ) : category === "पुष्प वाटिका" ? (
                    <i className="fas fa-seedling"></i>
                  ) : category === "बाग तराग" ? (
                    <i className="fas fa-water"></i>
                  ) : category === "उत्सव" ? (
                    <i className="fas fa-calendar-alt"></i>
                  ) : category === "भक्तगण" ? (
                    <i className="fas fa-users"></i>
                  ) : category === "विकास" ? (
                    <i className="fas fa-hammer"></i>
                  ) : category === "संस्कृति" ? (
                    <i className="fas fa-palette"></i>
                  ) : category === "प्राकृतिक सौंदर्य" ? (
                    <i className="fas fa-leaf"></i>
                  ) : category === "ऐतिहासिक" ? (
                    <i className="fas fa-scroll"></i>
                  ) : (
                    <i className="fas fa-image"></i>
                  )}
                </span>
                {category}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-center">
            <span className="text-temple-text-muted text-sm">
              {filteredImages.length} छवियां मिलीं
              {selectedCategory !== "सभी" && (
                <span className="text-temple-primary font-bold">
                  {" "}
                  "{selectedCategory}" में
                </span>
              )}
            </span>
          </div>
        </div>
      </section>

      {/* Enhanced Gallery Grid */}
      <section className="py-6 sm:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          {filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl sm:text-6xl mb-4">🖼️</div>
              <h3 className="text-lg sm:text-xl font-bold text-temple-text mb-2">
                कोई छवि उपलब्ध नहीं
              </h3>
              <p className="text-temple-text-muted text-sm sm:text-base">
                इस श्रेणी में अभी तक कोई छवि अपलोड नहीं की गई है।
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {filteredImages.map((image, index) => (
                <ScrollReveal
                  key={`${image._id}-${selectedCategory}`}
                  animation="scale"
                  delay={index * 50}
                  triggerOnce={false}
                  className="group cursor-pointer overflow-hidden rounded-2xl shadow-temple hover:shadow-temple-lg transition-all duration-300 hover:-translate-y-1 border border-temple-primary/10 bg-temple-bg-warm"
                  onClick={() => handleImageClick(image, index)}
                >
                  <div className="relative aspect-square">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-temple-primary/0 group-hover:bg-temple-primary/20 transition-colors duration-300" />

                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-temple-bg/90 text-temple-primary text-xs px-2 py-1 rounded-full font-bold backdrop-blur-sm">
                        {image.category}
                      </span>
                    </div>

                    {/* Overlay Info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-temple-primary/80 to-transparent p-3 sm:p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <h4 className="text-temple-bg font-bold text-sm sm:text-base mb-1 line-clamp-2">
                        {image.title}
                      </h4>
                      <p className="text-temple-bg/90 text-xs line-clamp-2">
                        {image.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-6xl max-h-full w-full">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white text-2xl hover:text-temple-secondary transition z-10 bg-black/50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              ✕
            </button>

            {/* Navigation Buttons */}
            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage("prev")}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-temple-secondary transition z-10 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center"
                >
                  ‹
                </button>
                <button
                  onClick={() => navigateImage("next")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-temple-secondary transition z-10 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center"
                >
                  ›
                </button>
              </>
            )}

            {/* Image Container */}
            <div className="flex flex-col lg:flex-row gap-6 max-h-full">
              {/* Image */}
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.title}
                  className="max-w-full max-h-[70vh] lg:max-h-[80vh] object-contain rounded-lg shadow-temple-lg"
                />
              </div>

              {/* Image Details Panel */}
              <div className="lg:w-80 bg-temple-bg-warm rounded-2xl p-6 shadow-temple max-h-[80vh] overflow-y-auto">
                <div className="space-y-4">
                  {/* Title and Category */}
                  <div>
                    <h3 className="text-xl font-bold text-temple-text mb-2">
                      {selectedImage.title}
                    </h3>
                    <span className="inline-block bg-temple-primary/10 text-temple-primary text-sm px-3 py-1 rounded-full font-bold">
                      {selectedImage.category}
                    </span>
                  </div>

                  {/* Description */}
                  <div>
                    <h4 className="font-bold text-temple-text mb-2">विवरण:</h4>
                    <p className="text-temple-text-muted text-sm leading-relaxed">
                      {selectedImage.description}
                    </p>
                  </div>

                  {/* Detailed Notes */}
                  {selectedImage.notes && (
                    <div>
                      <h4 className="font-bold text-temple-text mb-2 flex items-center">
                        <span className="mr-2">📝</span>
                        विस्तृत जानकारी:
                      </h4>
                      <p className="text-temple-text-muted text-sm leading-relaxed bg-temple-bg p-3 rounded-lg border border-temple-primary/10">
                        {selectedImage.notes}
                      </p>
                    </div>
                  )}

                  {/* Historical Significance */}
                  {selectedImage.historicalSignificance && (
                    <div>
                      <h4 className="font-bold text-temple-text mb-2 flex items-center">
                        <span className="mr-2 text-temple-primary">
                          <i className="fas fa-landmark"></i>
                        </span>
                        ऐतिहासिक महत्व:
                      </h4>
                      <p className="text-temple-text-muted text-sm leading-relaxed bg-temple-secondary/10 p-3 rounded-lg border border-temple-secondary/20">
                        {selectedImage.historicalSignificance}
                      </p>
                    </div>
                  )}

                  {/* Navigation Info */}
                  <div className="pt-4 border-t border-temple-primary/10">
                    <p className="text-temple-text-muted text-sm text-center">
                      छवि {currentIndex + 1} / {filteredImages.length}
                    </p>
                    <p className="text-temple-text-muted text-xs text-center mt-1">
                      नेवीगेशन के लिए ← → keys का उपयोग करें
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Information Section */}
      <section className="py-8 sm:py-12 bg-temple-bg-soft">
        <div className="max-w-3xl mx-auto px-4 sm:px-5 text-center">
          <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 border border-temple-secondary/20 shadow-temple">
            <div className="text-4xl mb-4 text-temple-primary">
              <i className="fas fa-camera-retro"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-temple-text mb-4">
              छवि संग्रह में योगदान दें
            </h3>
            <p className="text-temple-text-muted mb-6 leading-relaxed">
              यदि आपके पास फुलहर धाम की कोई दुर्लभ या सुंदर छवि है, तो कृपया
              हमारे साथ साझा करें। आपका योगदान इस पावन स्थल के इतिहास को संजोने
              में मदद करेगा।
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  if (user) {
                    setShowUploadModal(true);
                  } else {
                    toast.error("छवि अपलोड करने के लिए कृपया पहले लॉगिन करें");
                    navigate("/login");
                  }
                }}
                className="btn-temple-primary px-6 py-3 rounded-xl font-bold text-sm sm:text-base"
              >
                छवि साझा करें (Upload)
              </button>
              <button className="btn-temple-secondary px-6 py-3 rounded-xl font-bold text-sm sm:text-base">
                संपर्क करें
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-temple-bg w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-temple-primary/20">
            <div className="bg-temple-primary/10 px-6 py-4 border-b border-temple-primary/20 flex justify-between items-center">
              <h3 className="text-xl font-serif font-bold text-temple-primary">
                अपनी छवि साझा करें
              </h3>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="text-temple-text-muted hover:text-temple-accent transition-colors"
                disabled={uploading}
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <form 
              className="p-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!uploadForm.file || !uploadForm.title) {
                  toast.error("कृपया शीर्षक और छवि दोनों प्रदान करें");
                  return;
                }
                
                try {
                  setUploading(true);
                  
                  // 1. Upload to Firebase Storage
                  const url = await uploadToFirebase(
                    uploadForm.file,
                    `uploads/gallery/${Date.now()}_${uploadForm.file.name}`,
                    (progress) => setUploadProgress(progress)
                  );
                  
                  // 2. Submit to API
                  await galleryAPI.submitImage({
                    title: uploadForm.title,
                    description: uploadForm.description,
                    category: uploadForm.category,
                    url: url,
                    fileSize: uploadForm.file.size
                  });
                  
                  toast.success("आपकी छवि सफलतापूर्वक सबमिट हो गई है और व्यवस्थापक की मंजूरी की प्रतीक्षा में है।", { duration: 5000 });
                  setShowUploadModal(false);
                  setUploadForm({ title: "", description: "", category: "सामान्य", file: null });
                  setUploadProgress(0);
                  
                } catch (error) {
                  console.error("Upload error:", error);
                  toast.error(error.response?.data?.message || "छवि अपलोड करने में विफल");
                } finally {
                  setUploading(false);
                }
              }}
            >
              <div>
                <label className="block text-sm font-bold text-temple-text mb-1">शीर्षक (Title) *</label>
                <input 
                  type="text" 
                  required
                  maxLength={100}
                  className="w-full px-4 py-2 rounded-xl border border-temple-primary/20 bg-white focus:ring-2 focus:ring-temple-primary focus:border-transparent transition-all"
                  placeholder="चित्र का शीर्षक"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  disabled={uploading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-temple-text mb-1">श्रेणी (Category) *</label>
                <select 
                  className="w-full px-4 py-2 rounded-xl border border-temple-primary/20 bg-white focus:ring-2 focus:ring-temple-primary focus:border-transparent transition-all"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  disabled={uploading}
                >
                  {categories.filter(c => c !== "सभी").map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-temple-text mb-1">विवरण (Description)</label>
                <textarea 
                  rows="3"
                  maxLength={500}
                  className="w-full px-4 py-2 rounded-xl border border-temple-primary/20 bg-white focus:ring-2 focus:ring-temple-primary focus:border-transparent transition-all resize-none"
                  placeholder="चित्र के बारे में कुछ शब्द..."
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  disabled={uploading}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-temple-text mb-1">छवि (Image) *</label>
                <input 
                  type="file" 
                  required
                  accept="image/*"
                  className="w-full text-sm text-temple-text-muted
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-temple-primary/10 file:text-temple-primary
                    hover:file:bg-temple-primary/20 transition-all cursor-pointer"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  disabled={uploading}
                />
                <p className="text-xs text-temple-text-muted mt-2">अधिकतम फ़ाइल आकार: 5MB</p>
              </div>

              {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4">
                  <div className="bg-temple-primary h-2.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                  <p className="text-xs text-center mt-1 text-temple-text-muted">अपलोड हो रहा है: {Math.round(uploadProgress)}%</p>
                </div>
              )}

              <button 
                type="submit" 
                disabled={uploading || !uploadForm.file || !uploadForm.title}
                className="w-full btn-temple-primary py-3 rounded-xl font-bold mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'कृपया प्रतीक्षा करें...' : 'सबमिट करें'}
              </button>
              <p className="text-xs text-center text-temple-text-muted mt-3">
                आपकी छवि को वेबसाइट पर प्रदर्शित करने से पहले व्यवस्थापक द्वारा जाँचा जाएगा।
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Gallery Statistics */}
      <section className="py-8 sm:py-12 bg-temple-bg">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="text-center mb-8">
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-temple-text mb-4">
              गैलरी आंकड़े
            </h3>
            <div className="w-16 h-1 bg-temple-primary mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {categories.slice(1).map((category, index) => {
              const categoryCount = templeImages.filter(
                (img) => img.category === category
              ).length;
              return (
                <div
                  key={category}
                  className="bg-temple-bg-warm rounded-2xl p-4 text-center shadow-temple-sm border border-temple-primary/10"
                >
                  <div className="text-2xl mb-2">
                    {category === "गिरिजा स्थान"
                      ? "🕉️"
                      : category === "पुष्प वाटिका"
                      ? "🌸"
                      : category === "बाग तराग"
                      ? "💧"
                      : category === "उत्सव"
                      ? "🎉"
                      : category === "भक्तगण"
                      ? "👥"
                      : category === "विकास"
                      ? "🏗️"
                      : category === "संस्कृति"
                      ? "🎨"
                      : category === "प्राकृतिक सौंदर्य"
                      ? "🌿"
                      : category === "ऐतिहासिक"
                      ? "📜"
                      : "📷"}
                  </div>
                  <h4 className="font-bold text-temple-text text-sm mb-1">
                    {category}
                  </h4>
                  <p className="text-temple-primary font-bold text-lg">
                    {categoryCount}
                  </p>
                  <p className="text-temple-text-muted text-xs">छवियां</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
